import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, OnDestroy, Optional } from '@angular/core';
import { BYPASS_AUTH } from '@brand-lolly/app/_services/interceptors/http-config.interceptor';
import { smiUrl } from 'components';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportBlockData } from './report-blocks.service';
import { ReportLanguageService } from './report-language.service';
import { sourceSectionBatch } from './report-source-sections';

// TRAP: null is a read that FAILED, never "nothing" — a section with
// nothing in it is an empty list.
export type ReportSourceSectionAnswer = ReportBlockData[] | null;

interface ReportSectionsResolveRequest {
	sections: string[];
	from_date: string | null;
	to_date: string | null;
	language: string | null;
}

interface PendingRequest {
	reportId: string;
	from: string | null;
	to: string | null;
	language: string | null;
	sections: string[];
	answers: Array<[string, Observable<ReportSourceSectionAnswer>]>;
	result: ReplaySubject<Record<string, ReportBlockData[]> | null>;
}

/**
 * The fixed sections of one report drawn about its source — a competitor
 * project or an industry — read from `POST /reports/{id}/sections/resolve`
 * (report-source-sections.ts says how an answer is drawn).
 *
 * Read by the slide host and by the adapters it hands a section's slide. Both
 * ask for the same section and get the same answer: one read per section and
 * window, kept for the page, so present mode opened on the editor draws what
 * the editor drew without asking again. A failed read is not kept, and the
 * next ask makes it again.
 *
 * The share page needs nothing here: its token rides on every request under
 * `/reports/` (ReportShareSessionService). The PDF export has no session to
 * attach and says so once (`useExportToken`).
 */
// CONTRACT: provided by each page that draws a report — the editor, the PDF
// export, the shared page — one per page like ReportLanguageService. A page
// without one draws every section about the brand.
@Injectable()
export class ReportSourceSectionsService implements OnDestroy {
	private bypassAuth = false;
	private readonly answers = new Map<
		string,
		Observable<ReportSourceSectionAnswer>
	>();
	private readonly pending = new Map<string, PendingRequest>();
	private readonly requests = new Subscription();

	constructor(
		private http: HttpClient,
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	// CONTRACT: Gotenberg puts the export token on every request the headless
	// browser makes, so the interceptor must leave Authorization alone — as for
	// the blocks the report adds.
	useExportToken(): void {
		this.bypassAuth = true;
	}

	section(
		reportId: string,
		sectionId: string,
		from: Date | string | null | undefined,
		to: Date | string | null | undefined
	): Observable<ReportSourceSectionAnswer> {
		// TRAP: keyed on the instants — the slides hand over Dates and the host
		// strings, and keyed as given one window would be read twice.
		const start = instant(from);
		const end = instant(to);
		const language = this.reportLanguage?.language ?? null;
		const read = [reportId, start, end, language].join('|');
		const key = `${read}|${sectionId}`;
		const known = this.answers.get(key);
		if (known) {
			return known;
		}

		const requestKey = `${read}|${sourceSectionBatch(sectionId)}`;
		let request = this.pending.get(requestKey);
		if (!request) {
			request = {
				reportId,
				from: start,
				to: end,
				language,
				sections: [],
				answers: [],
				result: new ReplaySubject(1)
			};
			this.pending.set(requestKey, request);
			// TRAP: sent once the turn is over, not now — every slide of a page is
			// created in one pass, and the rest of the group joins this request.
			queueMicrotask(() => this.send(requestKey));
		}
		const answer = request.result.pipe(
			map(result => (result ? result[sectionId] ?? [] : null))
		);
		request.sections.push(sectionId);
		request.answers.push([key, answer]);
		this.answers.set(key, answer);
		return answer;
	}

	ngOnDestroy(): void {
		this.requests.unsubscribe();
	}

	private send(requestKey: string): void {
		const request = this.pending.get(requestKey);
		if (!request) {
			return;
		}
		this.pending.delete(requestKey);

		const url = smiUrl()
			.add('social-listening')
			.add('reports')
			.add(request.reportId)
			.add('sections')
			.add('resolve')
			.done();
		const body: ReportSectionsResolveRequest = {
			sections: request.sections,
			from_date: request.from,
			to_date: request.to,
			language: request.language
		};
		this.requests.add(
			this.http
				.post<Record<string, ReportBlockData[]> | null>(url, body, {
					context: this.bypassAuth
						? new HttpContext().set(BYPASS_AUTH, true)
						: undefined
				})
				.subscribe({
					next: result => request.result.next(result ?? {}),
					error: () => {
						// ORDER: forgotten before the failure is told, so a slide that
						// asks again on hearing it gets a read of its own.
						for (const [key, answer] of request.answers) {
							if (this.answers.get(key) === answer) {
								this.answers.delete(key);
							}
						}
						request.result.next(null);
						request.result.complete();
					},
					complete: () => request.result.complete()
				})
		);
	}
}

function instant(value: Date | string | null | undefined): string | null {
	if (!value) {
		return null;
	}
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
